import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Instagram } from "lucide-react";
import { BRAND } from "../lib/constants";
import SectionHeader from "./SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATIC_MODE = process.env.REACT_APP_STATIC === "true";

const initial = {
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
    consent: false,
};

export const Contact = () => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
            return;
        }

        if (STATIC_MODE) {
            const body = `Name: ${form.name}\nUnternehmen: ${form.company || "-"}\nE-Mail: ${form.email}\n\nNachricht:\n${form.message}`;
            const mailto = `mailto:${BRAND.email}?subject=${encodeURIComponent(form.subject || "Anfrage über die Website")}&body=${encodeURIComponent(body)}`;
            window.location.href = mailto;
            toast.success("Ihr E-Mail-Programm wird geöffnet…");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success(
                "Vielen Dank! Ihre Anfrage wurde übermittelt. Wir melden uns in Kürze."
            );
            setForm(initial);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            className="relative py-24 md:py-40 border-t border-ls-ink/10"
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                <div className="lg:col-span-5">
                    <SectionHeader
                        eyebrow="Kontakt"
                        title={
                            <>
                                Lassen Sie uns{" "}
                                <em className="italic font-light">sprechen</em>.
                            </>
                        }
                        intro="Schreiben Sie uns – wir melden uns innerhalb von 24 Stunden mit einem ersten Gesprächsvorschlag."
                    />
                    <div className="mt-12 space-y-6 font-sans">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-ls-inkSoft mb-2">
                                E-Mail
                            </div>
                            <a
                                href={`mailto:${BRAND.email}`}
                                className="font-serif text-2xl md:text-3xl text-ls-ink ls-underline"
                                data-testid="contact-email"
                            >
                                {BRAND.email}
                            </a>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-ls-inkSoft mb-2">
                                Standort
                            </div>
                            <div className="font-serif text-xl text-ls-ink">
                                Heidelberg · Deutschland
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-ls-inkSoft mb-2">
                                Instagram
                            </div>
                            <a
                                href={BRAND.instagram}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="inline-flex items-center gap-3 font-serif text-xl md:text-2xl text-ls-ink ls-underline"
                                data-testid="contact-instagram"
                            >
                                <Instagram size={20} />
                                {BRAND.instagramHandle}
                            </a>
                        </div>
                    </div>
                </div>

                <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    onSubmit={handleSubmit}
                    className="lg:col-span-7 bg-ls-surface p-8 md:p-12 border border-ls-ink/10 space-y-8"
                    data-testid="contact-form"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <Field
                            label="Name *"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            testid="contact-name"
                        />
                        <Field
                            label="E-Mail *"
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            required
                            testid="contact-email-input"
                        />
                        <Field
                            label="Unternehmen"
                            name="company"
                            value={form.company}
                            onChange={handleChange}
                            testid="contact-company"
                        />
                        <Field
                            label="Betreff *"
                            name="subject"
                            value={form.subject}
                            onChange={handleChange}
                            required
                            testid="contact-subject"
                        />
                    </div>

                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.24em] text-ls-inkSoft mb-2 font-sans">
                            Ihre Nachricht *
                        </label>
                        <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            rows={5}
                            required
                            className="w-full bg-transparent border-b border-ls-ink/20 focus:border-ls-ink outline-none py-3 font-sans text-base text-ls-ink placeholder:text-ls-inkSoft/50 transition-colors resize-none"
                            placeholder="Erzählen Sie uns kurz von Ihrem Vorhaben…"
                            data-testid="contact-message"
                        />
                    </div>

                    <label className="flex items-start gap-3 text-sm font-sans text-ls-inkSoft cursor-pointer">
                        <input
                            type="checkbox"
                            name="consent"
                            checked={form.consent}
                            onChange={handleChange}
                            className="mt-1 w-4 h-4 accent-ls-ink"
                            data-testid="contact-consent"
                        />
                        <span>
                            Ich stimme der Verarbeitung meiner Daten gemäß der{" "}
                            <a
                                href="/datenschutz"
                                className="underline hover:text-ls-ink"
                            >
                                Datenschutzerklärung
                            </a>{" "}
                            zu. *
                        </span>
                    </label>

                    <div className="flex items-center justify-between pt-4 border-t border-ls-ink/10">
                        <p className="text-xs text-ls-inkSoft font-sans">
                            * Pflichtfelder
                        </p>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group inline-flex items-center gap-3 bg-ls-ink text-ls-bg px-8 py-4 rounded-full text-xs uppercase tracking-[0.24em] font-sans hover:bg-ls-accent transition-all disabled:opacity-60"
                            data-testid="contact-submit"
                        >
                            {loading ? "Wird gesendet…" : "Anfrage senden"}
                            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                                →
                            </span>
                        </button>
                    </div>
                </motion.form>
            </div>
        </section>
    );
};

const Field = ({ label, testid, ...props }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-[0.24em] text-ls-inkSoft mb-2 font-sans">
            {label}
        </label>
        <input
            {...props}
            data-testid={testid}
            className="w-full bg-transparent border-b border-ls-ink/20 focus:border-ls-ink outline-none py-3 font-sans text-base text-ls-ink placeholder:text-ls-inkSoft/50 transition-colors"
        />
    </div>
);

export default Contact;
